import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

interface AuthInfoCardProps {
  icon?: LucideIcon;
  title: string;
  children?: ReactNode;
  /** Optional supplementary mono label (e.g. "WORKSPACE ID · org_abc123"). */
  monoMeta?: string;
}

/**
 * AuthInfoCard — nested info surface inside an auth action area.
 * Used for verification-sent confirmation, invite-card body,
 * magic-link-sent confirmation, etc.
 *
 * Uses the editorial card radius + shadow-xs lift to feel native to the
 * dashboard's `.adp-card-item` register.
 */
export function AuthInfoCard({ icon: Icon, title, children, monoMeta }: AuthInfoCardProps) {
  return (
    <div className="auth-info-card">
      <div className="flex items-start gap-3">
        {Icon && (
          <Icon
            className="w-5 h-5 mt-0.5 flex-shrink-0 text-[var(--brand)]"
            aria-hidden="true"
          />
        )}
        <div className="flex-1 min-w-0">
          <h3
            className="text-base font-semibold text-[var(--foreground)] mb-1"
            style={{ letterSpacing: "-0.01em" }}
          >
            {title}
          </h3>
          {children && (
            <div
              className="text-sm text-[var(--muted-foreground)]"
              style={{ lineHeight: 1.55 }}
            >
              {children}
            </div>
          )}
        </div>
      </div>
      {monoMeta && (
        <p className="auth-mono-label mt-3 pt-3 border-t border-[var(--border)]">
          {monoMeta}
        </p>
      )}
    </div>
  );
}
